import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';

const WEEKDAYS = [
  { code: 'MO', label: 'Monday' },
  { code: 'TU', label: 'Tuesday' },
  { code: 'WE', label: 'Wednesday' },
  { code: 'TH', label: 'Thursday' },
  { code: 'FR', label: 'Friday' },
  { code: 'SA', label: 'Saturday' },
  { code: 'SU', label: 'Sunday' },
];
const MODE_FALLBACK = 'fallback';
const MODE_TIME = 'time';
const MODE_NO_DISMISSAL = 'no_dismissal';

function toInputValue(value) {
  if (!value) return '';
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

function toApiValue(inputValue) {
  if (!inputValue) return null;
  const match = String(inputValue).match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  return `${match[1].padStart(2, '0')}:${match[2]}:00`;
}

function isSchoolWideDefault(row) {
  return row && (row.grade === null || row.grade === undefined) && (row.gender === null || row.gender === undefined);
}

function isNoDismissalDefault(row) {
  return row?.is_no_dismissal === true
    || row?.is_no_dismissal === 1
    || String(row?.is_no_dismissal) === '1';
}

function buildRowsByWeekday(defaults = []) {
  const rowsByWeekday = new Map(WEEKDAYS.map((day) => [day.code, []]));

  defaults
    .filter(isSchoolWideDefault)
    .sort((left, right) => (
      Number(isNoDismissalDefault(right)) - Number(isNoDismissalDefault(left))
        || String(left.dismissal_time || '').localeCompare(String(right.dismissal_time || ''))
        || Number(left.id || 0) - Number(right.id || 0)
    ))
    .forEach((row) => {
      if (rowsByWeekday.has(row.weekday)) {
        rowsByWeekday.get(row.weekday).push(row);
      }
    });

  return rowsByWeekday;
}

function buildInputValues(defaults = []) {
  const rowsByWeekday = buildRowsByWeekday(defaults);
  return Object.fromEntries(WEEKDAYS.map((day) => [
    day.code,
    (() => {
      const row = rowsByWeekday.get(day.code)?.[0];
      if (!row) return { mode: MODE_FALLBACK, time: '' };
      if (isNoDismissalDefault(row)) return { mode: MODE_NO_DISMISSAL, time: '' };
      return { mode: MODE_TIME, time: toInputValue(row.dismissal_time) };
    })(),
  ]));
}

export default function SchoolWeeklyDismissalDefaultsField({
  schoolId,
  defaults = [],
  token,
  onSaved,
  disabled = false,
}) {
  const [values, setValues] = useState(() => buildInputValues(defaults));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(0);

  const initialValues = useMemo(() => buildInputValues(defaults), [defaults]);
  const rowsByWeekday = useMemo(() => buildRowsByWeekday(defaults), [defaults]);
  const dirty = JSON.stringify(values) !== JSON.stringify(initialValues);

  useEffect(() => {
    setValues(buildInputValues(defaults));
  }, [defaults]);

  const setDayMode = (weekday) => (event) => {
    const mode = event.target.value;
    setValues((current) => ({
      ...current,
      [weekday]: {
        mode,
        time: mode === MODE_TIME ? (current[weekday]?.time || '') : '',
      },
    }));
  };

  const setDayTime = (weekday) => (event) => {
    setValues((current) => ({
      ...current,
      [weekday]: {
        mode: MODE_TIME,
        time: event.target.value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const savedRows = [];

      for (const day of WEEKDAYS) {
        const existingRows = rowsByWeekday.get(day.code) || [];
        const [primaryRow, ...extraRows] = existingRows;
        const dayValue = values[day.code] || { mode: MODE_FALLBACK, time: '' };
        const apiValue = dayValue.mode === MODE_TIME ? toApiValue(dayValue.time) : null;
        const isNoDismissal = dayValue.mode === MODE_NO_DISMISSAL;

        if (dayValue.mode === MODE_TIME && !apiValue) {
          throw new Error(`Enter a dismissal time for ${day.label}.`);
        }

        if (apiValue || isNoDismissal) {
          const body = {
            school_id: Number(schoolId),
            grade: null,
            gender: null,
            weekday: day.code,
            valid_from: '1900-01-01',
            valid_to: null,
            dismissal_time: isNoDismissal ? null : apiValue,
            is_no_dismissal: isNoDismissal,
          };

          const response = primaryRow
            ? await apiFetch(`/dismissal-time-defaults/${primaryRow.id}`, {
                method: 'PUT',
                body: JSON.stringify(body),
              }, token)
            : await apiFetch('/dismissal-time-defaults', {
                method: 'POST',
                body: JSON.stringify(body),
              }, token);

          if (response?.data) savedRows.push(response.data);
        }

        if ((!apiValue && !isNoDismissal) || extraRows.length > 0) {
          const rowsToDelete = (apiValue || isNoDismissal) ? extraRows : existingRows;
          for (const row of rowsToDelete) {
            await apiFetch(`/dismissal-time-defaults/${row.id}`, { method: 'DELETE' }, token);
          }
        }
      }

      onSaved?.(savedRows);
      setSavedAt(Date.now());
    } catch (err) {
      setError(err.message || 'Could not save weekday dismissal defaults');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ssp-weekly-defaults">
      <div className="ssp-weekly-defaults-grid">
        {WEEKDAYS.map((day) => (
          <label key={day.code} className="ssp-default-time-field">
            <span>{day.label}</span>
            <select
              value={values[day.code]?.mode || MODE_FALLBACK}
              onChange={setDayMode(day.code)}
              disabled={disabled || saving}
            >
              <option value={MODE_FALLBACK}>Use default</option>
              <option value={MODE_TIME}>Set time</option>
              <option value={MODE_NO_DISMISSAL}>No dismissal</option>
            </select>
            {(values[day.code]?.mode || MODE_FALLBACK) === MODE_TIME ? (
              <input
                type="time"
                value={values[day.code]?.time || ''}
                onChange={setDayTime(day.code)}
                disabled={disabled || saving}
                required
              />
            ) : null}
          </label>
        ))}
      </div>

      <div className="ssp-default-times-actions">
        <button
          type="button"
          className="sa-btn sa-btn-primary"
          onClick={handleSave}
          disabled={disabled || saving || !dirty}
        >
          {saving ? 'Saving...' : 'Save Weekdays'}
        </button>
        {!dirty && savedAt ? <span className="ssp-default-times-saved">Saved</span> : null}
      </div>

      {error ? <div className="sa-error" style={{ marginTop: 6 }}>{error}</div> : null}
    </div>
  );
}
